<template>
  <div class="bar-modal" ref="barModal" :style="{ top: `-${topOffset}px` }">
    <button class="bar-modal__close" type="button" @click="$emit('close')">
      <Close class="bar-modal__close-icon" />
    </button>
    <div class="bar-modal__info">
      <h3 class="bar-modal__info-title">Quiz complete!</h3>
      <!-- <p>S:{{ score }} SS:{{ slatesterScore }} AS:{{ averageScore }}</p> -->
      <p class="bar-modal__info-questions">
        You answered
        <strong>
          {{ numCorrect }} out of {{ numQuestions }} questions correctly
        </strong>
        and received
        <strong>{{ score }} points.</strong>
      </p>
    </div>
    <div class="bar-modal__graph">
      <div class="score-graph">
        <div class="player-axis">
          <div class="player-label">
            <Avatar
              :name="username || 'You'"
              :title="userLabel || 'Slate reader'"
              :image="slateReaderImg"
              :crown="highestScore === score && !!quizData.slatester_name"
            />
          </div>
          <div class="player-label" v-if="quizData.slatester_name">
            <Avatar
              :name="quizData.slatester_name"
              :title="quizData.slatester_title"
              :image="quizData.slatester_image"
              :crown="highestScore === slatesterScore"
            />
          </div>
          <div class="player-label">
            <Avatar
              name="Average score"
              :image="avgScoreImg"
              :crown="highestScore === averageScore"
            />
          </div>
          <!-- coupla edge cases, comment out before deploying -->
          <!--div class="player-label">
						<Avatar
							name="Long word"
							title="National Correspondent"
							image="https://compote.slate.com/images/0283abed-887c-47ad-8f9e-f3e0783f5b7b.jpeg?width=300"
						/>
					</div>
					<div class="player-label">
						<Avatar
							name="Long title"
							title="National Editor and Host of One Year"
							image="https://compote.slate.com/images/461ff9b0-8e38-4bd1-82cd-ffbd0251013f.jpeg?width=300"
						/>
					</div-->
        </div>

        <div class="bars">
          <div ref="userBar" :class="userBarClass()">
            <strong class="score-label" ref="userScore">
              {{ score }}
            </strong>
            <strong
              v-if="score === highestScore && !!quizData.slatester_name"
              class="winner-label"
            >
              Winner!
            </strong>
          </div>

          <div
            v-if="quizData.slatester_name"
            ref="slatesterBar"
            :class="barClass(slatesterScore)"
          >
            <strong class="score-label" ref="slatesterScore">
              {{ slatesterScore }}
            </strong>
            <strong
              v-if="
                slatesterScore === highestScore && !!quizData.slatester_name
              "
              class="winner-label"
            >
              Winner!
            </strong>
          </div>

          <div ref="averageBar" :class="barClass(averageScore)">
            <strong class="score-label" ref="averageScore">
              {{ averageScore }}
            </strong>
          </div>
        </div>
      </div>
    </div>
    <ScoreShare :score="score" />
  </div>
</template>
<script>
import Close from "./Close.vue";
import Avatar from "./Avatar.vue";
import ScoreShare from "./ScoreShare.vue";
import { prideClass } from "../pride.js";
import { INITIAL_TIME as MAX_QUESTION_SCORE } from "../constants.js";
import { scrollElIntoView } from "../utils.js";
import slateReaderImg from "@/assets/slate_reader.png";
import avgScoreImg from "@/assets/average_score.png";

export default {
  name: "BarModal",
  components: { Close, Avatar, ScoreShare },
  props: {
    quizData: { type: Object, required: true },
    username: { type: String, required: true },
    userLabel: { type: String, required: true },
    score: { type: Number, required: true },
    averageScore: { type: Number, required: true },
    numQuestions: { type: Number, required: true },
    numCorrect: { type: Number, required: true },
  },
  computed: {
    // alias for more consistent naming
    slatesterScore() {
      return Number(this.quizData.slatester_score);
    },
  },
  data() {
    return {
      highestScore: 0,
      topOffset: 0,
      slateReaderImg,
      avgScoreImg,
    };
  },
  mounted() {
    this.animateChart();
    this.getModalheight();
    scrollElIntoView(this.$refs.barModal);
  },
  methods: {
    userBarClass() {
      const barClass = this.barClass(this.score);
      if (this.score === this.highestScore) {
        const maybePrideClass = prideClass();
        if (maybePrideClass) {
          barClass[maybePrideClass] = true;
        }
      }
      return barClass;
    },
    scaleScore(score) {
      return (score / (MAX_QUESTION_SCORE * this.numQuestions)) * 100;
    },
    barClass(score) {
      return {
        bar: true,
        "bar--inferior": this.scaleScore(score) <= 10,
      };
    },
    getModalheight() {
      const height = this.$refs.barModal.clientHeight;
      this.topOffset = height;
    },
    async animateChart() {
      const items = [
        // tuple of bar (el), score/label (el), score (number), canWin (boolean)
        [this.$refs.userBar, this.$refs.userScore, this.score, true],
        [
          this.$refs.slatesterBar,
          this.$refs.slatesterScore,
          this.slatesterScore,
          true,
        ],
        [
          this.$refs.averageBar,
          this.$refs.averageScore,
          this.averageScore,
          false,
        ],
      ].filter((tuple) => Boolean(tuple[0]));

      const highestScore = Math.max(this.score, this.slatesterScore);

      await Promise.all(
        // n.b. map plays each animation in parallel, use "for" loop if you want them to be sequential
        items.map(async ([$bar, $score, score, canWin]) => {
          const scaledScore = this.scaleScore(score);

          const barDuration = scaledScore * 20;
          const barKeyframes = [
            { transform: "translateX(-100%)" },
            { transform: `translateX(${scaledScore - 100}%)` },
          ];
          const barOptions = {
            duration: barDuration,
            easing: "ease-out",
            fill: "both",
          };
          await $bar.animate(barKeyframes, barOptions).finished;

          if (score === highestScore && canWin) {
            $bar.classList.add("bar--winner");
          }

          const scoreKeyframes = [
            { transform: "translateX(-0.4em)", opacity: 0 },
            { opacity: 1 },
          ];
          const scoreOptions = {
            duration: 120,
            easing: "ease",
            fill: "both",
          };
          return $score.animate(scoreKeyframes, scoreOptions).finished;
        })
      );

      this.highestScore = highestScore;
    },
  },
};
</script>
<style scoped lang="scss">
@use "sass:math";

.bar-modal {
  display: block;
  position: absolute;
  background-color: var(--interactive-scoreboard-color);
  width: 90%;
  max-width: 700px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, 10%);
  z-index: 1;
  padding: 1.5rem;
  border-radius: 10px;
  border: 1px solid var(--interactive-border-color);
  filter: drop-shadow(0 0 15px #00000040);
}

.bar-modal__close {
  position: absolute;
  right: 1.5rem;
  border: none;
  background: none;
  height: 1.2em;
  fill: #ccc;
  padding: 0;
  width: 1.2em;
  transition: opacity 0.12s ease;

  &:focus,
  &:hover {
    opacity: 0.6;
  }
}

.bar-modal__close-icon {
  height: 100%;
  width: 100%;
}

.bar-modal__info {
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;
}

.bar-modal__info-title {
  margin: 0 0 0.5em;
  @media (max-width: 35em) {
    font-size: 1.138em;
  }
}

.bar-modal__info-questions {
  margin: 0 0 1em;
  max-width: 350px;
  text-align: center;
  @media (max-width: 35em) {
    font-size: 0.823em;
  }
}

.bar-modal__graph {
  display: flex;
  justify-content: center;
  align-items: center;
}
.score-graph {
  display: flex;
  width: 100%;
  max-width: 620px;
}

.player-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  max-width: max-content;
  min-width: 7em; // enough space for "Average score" to render w/o breaking a line
  max-width: 35%;

  // sorry this defies component encapsulation, orient the players vertically once there's no longer enough room to display a word as long as "Correspondent", e.g. Will Saletan, National Correspondent
  // @media (max-width: 35em) {
  //   .avatar {
  //     outline: 1px solid tomato;
  //   }
  // }
}

// .player-label {
//   margin-bottom: 0.5rem;
// }

.bars {
  flex-grow: 1;
  margin-left: 0.4em;
  border-left: 1px solid #ccc;
  border-color: #{"hsla(var(--txt-color-hsl), 0.2)"};
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  overflow: hidden;

  padding-right: 5em; /* leave room on the end for the "Winner!" label */

  @media (max-width: 35em) {
    padding-right: 0;
  }
}

$stripe-size: 2.1rem;
$bar-size: $stripe-size;

.bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  transform: translateX(-100%);
  position: relative;
  background-color: #888; // gray-c
  color: white;
  height: $bar-size;
  width: 100%;
  transition: background-color 0.12s ease;

  &--winner {
    background-color: #ff0e50; // cherry
  }
}

$pride-colors: (
  juneteenth: #1e325f white #ce2430,
  women: #46286e white #589c48,
  // https://en.wikipedia.org/wiki/Pride_flag
  agender: black #b9b9b9 #fff #b8f483 #fff #b9b9b9 black,
  asexual: black #a3a3a3 white purple,
  lesbien: #d52d00 #ff9a56 white #d362a4 #a30262,
  lgbt: black #784f17 #e40303 #ff8c00 #ffed00 #008026 #004dff #750787,
  non-binary: #fcf434 #fcfcfc #9c59d1 #2c2c2c,
  trans: #5bcefa #f5a9b8 white #f5a9b8 #5bcefa,
);

.bar.pride {
  // the size varies depending on the number of colors in the flag,
  // i put it in a custom property so we can use the same animation for all
  background-size: var(--full-size) $bar-size;
  background-position: 0 0;
  animation: 3s linear infinite wave;

  @media (prefers-reduced-motion: reduce) {
    animation-play-state: paused;
  }
}

@keyframes wave {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: var(--full-size) 0;
  }
}

@each $name, $colors in $pride-colors {
  .bar.#{$name} {
    $i: 0 - math.div($stripe-size, 2);
    $color-stops: ();
    @each $c in $colors {
      $color-stops: append($color-stops, $c $i, comma);
      $i: $i + $stripe-size;
      $color-stops: append($color-stops, $c $i, comma);
    }

    background-image: repeating-linear-gradient(45deg, $color-stops);
    --full-size: #{length($colors) *
      $stripe-size *
      1.402380952}; // honestly no idea how this modifier value is derived, some function of 45º - need to learn geometry again!
  }
}

.score-label {
  margin: 0 0.5em;
  opacity: 0;

  .bar--inferior & {
    transform: translateX(2.2em);
    color: black;
    color: var(--txt-color);
  }
}

.winner-label {
  align-self: center;
  /* so it doesn't contribute to width, and the bar transform is normalized */
  position: absolute;
  left: 100%;
  margin-left: 0.6em;
  color: black;
  color: var(--txt-color);

  @media (max-width: 35em) {
    top: calc(100% + 0.4em);
    right: 0;
    left: auto;
  }
}
</style>
